import Analytics from './analytics';
import moment from 'moment-timezone';
import { AdCampaignEventType } from '@/enums';
import {
  AdCampaignModuleType,
  AdEventInput,
  SampleRequestVariantPackFieldsFragment,
} from '@/types';
import { formatDate } from './date';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';

export interface VariantPackEventPayloads {
  addToCartEventPayloads: AdEventInput[];
  purchaseEventPayloads: AdEventInput[];
}

interface AdTrackingData {
  adCampaignUUID: string;
  adManagerUUID: string;
  adModule: AdCampaignModuleType;
  advertiserUUID: string;
  distributorUUID: string;
  eventType: AdCampaignEventType;
  eventUUID: string;
  gtin: string;
  keyword?: string;
  operatorUUID: string;
  source: string;
  userUUID: string;
  variantPackUUID: string;
}

export const trackAdAnalytics = (data: AdTrackingData) => {
  Analytics.track(`ads.${data.adModule}.${data.eventType}`.toLowerCase(), data);
};

export const findFirstPaidSearchSku = (item: ItemListOption) =>
  item.metadata?.skus.find((sku) => sku.paidSearch);

export const getAddedVariantPackEventPayloads = (
  metadata: ItemListOptionMetadata,
): VariantPackEventPayloads | null => {
  const addToCartEventPayloads = [];
  const purchaseEventPayloads = [];

  if (metadata.displayAd) {
    const displayAd = metadata.displayAd;
    addToCartEventPayloads.push({
      ad_campaign_id: displayAd.ad_campaign_id,
      ad_manager_id: displayAd.ad_manager_id,
      ad_module: AdCampaignModuleType.DisplayAd,
      advertiser_id: displayAd.advertiser_id,
      event_id: displayAd.variant_pack_event_data.add_to_cart_event_id,
      gtin: displayAd.gtin,
    });
    purchaseEventPayloads.push({
      ad_campaign_id: displayAd.ad_campaign_id,
      ad_manager_id: displayAd.ad_manager_id,
      ad_module: AdCampaignModuleType.DisplayAd,
      advertiser_id: displayAd.advertiser_id,
      event_id: displayAd.variant_pack_event_data.purchase_event_id,
      gtin: displayAd.gtin,
    });
  }
  if (metadata.paidSearch) {
    const paidSearch = metadata.paidSearch;
    addToCartEventPayloads.push({
      ad_campaign_id: paidSearch.ad_campaign_id,
      ad_manager_id: paidSearch.ad_manager_id,
      ad_module: AdCampaignModuleType.PaidSearch,
      advertiser_id: paidSearch.advertiser_id,
      event_id: paidSearch.add_to_cart_event_id,
      gtin: paidSearch.gtin,
      keyword: paidSearch.matching_keyword,
    });
    purchaseEventPayloads.push({
      ad_campaign_id: paidSearch.ad_campaign_id,
      ad_manager_id: paidSearch.ad_manager_id,
      ad_module: AdCampaignModuleType.PaidSearch,
      advertiser_id: paidSearch.advertiser_id,
      event_id: paidSearch.purchase_event_id,
      gtin: paidSearch.gtin,
      keyword: paidSearch.matching_keyword,
    });
  }
  if (metadata.sampleRequest) {
    const sampleRequest = metadata.sampleRequest;
    addToCartEventPayloads.push({
      ad_campaign_id: sampleRequest.ad_campaign_id,
      ad_manager_id: sampleRequest.ad_manager_id,
      ad_module: AdCampaignModuleType.SampleRequest,
      advertiser_id: sampleRequest.advertiser_id,
      event_id: sampleRequest.sample_request_event_data.add_to_cart_event_id,
      gtin: sampleRequest.gtin,
    });
    purchaseEventPayloads.push({
      ad_campaign_id: sampleRequest.ad_campaign_id,
      ad_manager_id: sampleRequest.ad_manager_id,
      ad_module: AdCampaignModuleType.SampleRequest,
      advertiser_id: sampleRequest.advertiser_id,
      event_id: sampleRequest.sample_request_event_data.purchase_event_id,
      gtin: sampleRequest.gtin,
    });
  }

  return addToCartEventPayloads.length > 0 || purchaseEventPayloads.length > 0
    ? {
        addToCartEventPayloads,
        purchaseEventPayloads,
      }
    : null;
};

export const getNextAvailableSampleRequestDate = (
  earliestRequestAt: string | null | undefined,
  lookbackDays: number | null | undefined,
  timezone: string,
) => {
  if (
    typeof earliestRequestAt !== 'string' ||
    typeof lookbackDays !== 'number'
  ) {
    return null;
  }

  const earliestRequestDate = moment(earliestRequestAt);
  return formatDate(earliestRequestDate.tz(timezone).add(lookbackDays, 'days'));
};

export const isSampleRequestLimitReached = (
  sampleRequest: SampleRequestVariantPackFieldsFragment | undefined,
) =>
  typeof sampleRequest?.request_limit === 'number' &&
  typeof sampleRequest?.request_count === 'number' &&
  sampleRequest.request_limit <= sampleRequest.request_count;
